/**
 * User Role Enum
 * 
 * Defines all possible user roles in the system.
 * Eliminates magic strings and provides type safety.
 */
export enum UserRole {
  Owner = 'Owner',
  Admin = 'Admin',
  Contributor = 'Contributor',
  Operator = 'Operator',
  Viewer = 'Viewer',
  Unknown = 'Unknown'
}

/**
 * Role ID Enum
 * 
 * Maps role IDs to their corresponding roles.
 * These IDs come from the backend API.
 */
export enum RoleId {
  Operator = 10,
  Contributor = 11,
  Admin = 12
}

/**
 * Role Helper
 * 
 * Provides utilities for working with user roles.
 * Centralizes role-related logic for consistency.
 */
export class RoleHelper {
  /**
   * Get the icon path for a role
   * @param role User role
   * @returns Path to the role icon SVG
   */
  static getRoleIconPath(role: UserRole | string): string {
    const roleStr = typeof role === 'string' ? role : String(role);
    
    switch (roleStr) {
      case UserRole.Owner:
        return 'assets/icons/owner.svg';
      case UserRole.Admin:
        return 'assets/icons/admin.svg';
      case UserRole.Contributor:
        return 'assets/icons/contributor.svg';
      case UserRole.Viewer:
      case UserRole.Operator:
        return 'assets/icons/operator.svg';
      default:
        return '';
    }
  }

  /**
   * Get the icon name for Material Icons
   * @param role User role
   * @returns Material icon name
   */
  static getRoleIcon(role: UserRole | string): string {
    const roleStr = typeof role === 'string' ? role : String(role);
    
    switch (roleStr) {
      case UserRole.Owner:
        return 'role-owner';
      case UserRole.Admin:
        return 'role-admin';
      case UserRole.Contributor:
        return 'role-contributor';
      case UserRole.Operator:
      case UserRole.Viewer:
        return 'role-operator';
      default:
        return 'role-admin';
    }
  }

  /**
   * Get role name from role ID
   * @param roleId Role ID from backend
   * @returns Role name
   */
  static getRoleNameFromId(roleId: number): string {
    switch (roleId) {
      case RoleId.Admin:
        return UserRole.Admin;
      case RoleId.Contributor:
        return UserRole.Contributor;
      case RoleId.Operator:
        return UserRole.Operator;
      default:
        return UserRole.Unknown;
    }
  }

  /**
   * Get display name for a role (capitalized)
   * @param role User role
   * @returns Capitalized role name
   */
  static getDisplayName(role: UserRole | string): string {
    const roleStr = typeof role === 'string' ? role : String(role);
    return roleStr.charAt(0).toUpperCase() + roleStr.slice(1).toLowerCase();
  }

  /**
   * Parse role from string (case-insensitive)
   * @param roleStr Role string
   * @returns UserRole enum value
   */
  static parseRole(roleStr: string): UserRole {
    const normalized = roleStr.toLowerCase();
    
    if (normalized.includes('owner')) return UserRole.Owner;
    if (normalized.includes('admin')) return UserRole.Admin;
    if (normalized.includes('contributor')) return UserRole.Contributor;
    if (normalized.includes('operator')) return UserRole.Operator;
    if (normalized.includes('viewer')) return UserRole.Viewer;
    
    return UserRole.Unknown;
  }
}

